export type RecordingListenerHandle = {
  remove: () => Promise<void>;
};

type CoreRecordingPlugin = {
  addListener(
    event: "onStreamStatusChange",
    listener: (event: any) => void
  ): Promise<RecordingListenerHandle> | RecordingListenerHandle;
  addListener(
    event: "onAudioLevelChange",
    listener: (event: { audioLevel: number }) => void
  ): Promise<RecordingListenerHandle> | RecordingListenerHandle;
};

type IosInterruptionPlugin = {
  addListener(
    event: "onInterruptionBegan" | "onInterruptionEnded",
    listener: () => void
  ): Promise<RecordingListenerHandle> | RecordingListenerHandle;
};

const removeHandles = async (handles: RecordingListenerHandle[]) => {
  await Promise.allSettled(handles.map((handle) => handle.remove()));
};

export const attachNativeRecordingListeners = async (
  plugin: CoreRecordingPlugin,
  handlers: {
    onStatus: (event: any) => void;
    onAudioLevel: (event: { audioLevel: number }) => void;
  }
): Promise<RecordingListenerHandle[]> => {
  const handles: RecordingListenerHandle[] = [];
  try {
    handles.push(
      await /* action-id: lifecycle.recording.status */ plugin.addListener(
        "onStreamStatusChange",
        handlers.onStatus
      )
    );
    handles.push(
      await /* action-id: lifecycle.recording.audioLevel */ plugin.addListener(
        "onAudioLevelChange",
        handlers.onAudioLevel
      )
    );
    return handles;
  } catch (error) {
    await removeHandles(handles);
    throw error;
  }
};

export const attachIosInterruptionListeners = async (
  plugin: IosInterruptionPlugin,
  handlers: { onBegan: () => void; onEnded: () => void }
): Promise<RecordingListenerHandle[]> => {
  const handles: RecordingListenerHandle[] = [];
  try {
    handles.push(
      await /* action-id: lifecycle.recording.interruption */ plugin.addListener(
        "onInterruptionBegan",
        handlers.onBegan
      )
    );
    handles.push(
      await /* action-id: lifecycle.recording.interruption */ plugin.addListener(
        "onInterruptionEnded",
        handlers.onEnded
      )
    );
    return handles;
  } catch (error) {
    await removeHandles(handles);
    throw error;
  }
};

type BrowserEventTarget = {
  addEventListener: (event: string, listener: EventListener) => void;
  removeEventListener: (event: string, listener: EventListener) => void;
};

export const attachRecordingNetworkListeners = (
  target: BrowserEventTarget,
  handlers: { online: EventListener; offline: EventListener }
): (() => void) => {
  /* action-id: lifecycle.recording.network */ target.addEventListener(
    "online",
    handlers.online
  );
  /* action-id: lifecycle.recording.network */ target.addEventListener(
    "offline",
    handlers.offline
  );
  return () => {
    target.removeEventListener("online", handlers.online);
    target.removeEventListener("offline", handlers.offline);
  };
};

export const attachRecordingVisibilityListener = (
  target: BrowserEventTarget,
  listener: EventListener
): (() => void) => {
  /* action-id: lifecycle.recording.visibility */ target.addEventListener(
    "visibilitychange",
    listener
  );
  return () => target.removeEventListener("visibilitychange", listener);
};

export const attachRecordingBeforeUnloadListener = (
  target: BrowserEventTarget,
  listener: EventListener
): (() => void) => {
  /* action-id: lifecycle.recording.beforeUnload */ target.addEventListener(
    "beforeunload",
    listener
  );
  return () => target.removeEventListener("beforeunload", listener);
};

export const attachRecordingDeviceChangeListener = (
  target: BrowserEventTarget,
  listener: EventListener
): (() => void) => {
  /* action-id: lifecycle.recording.deviceChange */ target.addEventListener(
    "devicechange",
    listener
  );
  return () => target.removeEventListener("devicechange", listener);
};
